import type { HttpAgent } from "@ag-ui/client";
import type { ApolloClient } from "@apollo/client";
import { fetchCatalogue, openGame, readGame } from "./api";
import {
  roleplayErrorCode,
  type GameResponse,
  type GameSummary,
  type NarratorFailure,
  type RoleplayErrorCode,
  type RunResult,
} from "./contract";
import { gameEntry } from "./entry";
import { canSend, createRoleplayGameStore, hasRun, type Notice, type RetryAction, type RoleplayGameStore } from "./game-store";
import { genesisRoleplayRefusal, lockRoleplaySessionPowers, restartRoleplaySession } from "./genesis";
import { startPlaneGame } from "./new-game";
import { turnText } from "./story";
import {
  createNarrationAgent,
  createTurnAgent,
  runRoleplayNarration,
  runRoleplayTurn,
  type RoleplayRunHandlers,
} from "./turn";

// What the game page does with one game (play contract 3): open it (B3), play turns (B5), tell every owed narration
// by itself with no click (F6, O2), find a stalled run again (§4.5), lock the Powers before the first move, follow the
// story's end until genesis has its evaluation (B4), restart (G2) or start a new game after an expired one (B1, G1).
// Nothing ends a run early: the skip (B7) is gone, and a game is continued later (spark#1075).

/**
 * §4.5: while another turn or narration holds the game, B4 every 2 s, with no fixed limit: a turn may take as long as
 * the AI needs, and the run that holds the game's lock renews it with a heartbeat while it works (§5.3,
 * agent-service#129, !113). spark waits as long as B4 says `game.runAlive`; a field that is missing (an older
 * agent-service) counts as alive. The wait ends when the run is no longer alive and has not stored the turn, or when
 * B4 has failed for POLL_UNREACHABLE_MS in a row: the server is not responding.
 */
export const POLL_INTERVAL_MS = 2_000;
export const POLL_UNREACHABLE_MS = 30_000;

export interface RoleplaySession {
  store: RoleplayGameStore;
  start: () => void;
  stop: () => void;
  refresh: () => Promise<void>;
  send: (client: ApolloClient, onLocked?: () => void) => Promise<void>;
  retry: () => Promise<void>;
  restart: (client: ApolloClient) => Promise<string | null>;
  continueTo: (client: ApolloClient) => Promise<string | null>;
}

type Outcome =
  | { kind: "finished"; result: RunResult }
  | { kind: "failed"; code: RoleplayErrorCode; reason?: NarratorFailure }
  | { kind: "unknown" };

// A read that settled: the game, and whether the run that held it died before releasing it (§4.5). Or the notice the
// check ends with.
type Read = { response: GameResponse; runDied: boolean } | { notice: Notice };

// Answers of B4 that say nothing about the game: the server or the way to it is down for now (§5.1, §5.2). Polls that
// fail like this are tried again until they have failed for POLL_UNREACHABLE_MS; any other refusal is an answer.
const UNREACHABLE: ReadonlySet<RoleplayErrorCode> = new Set(["UNKNOWN", "ROLEPLAY_UNAVAILABLE", "ROLEPLAY_GENESIS_UNAVAILABLE"]);
const NOT_RESPONDING: Notice = { code: "notResponding", retry: "check" };

// Ends a flow whose page left (or opened the game again) while it waited, before it touches the store.
class Stale extends Error {}

function rethrowStale(error: unknown): void {
  if (error instanceof Stale) throw error;
}

async function unlessStale<T>(work: Promise<T>, fallback: T): Promise<T> {
  try {
    return await work;
  } catch (error) {
    if (error instanceof Stale) return fallback;
    throw error;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** The neutral message a refusal leaves on the page. */
export function noticeFor(code: RoleplayErrorCode, retry: RetryAction | null): Notice {
  switch (code) {
    case "ROLEPLAY_GAME_EXPIRED":
      return { code: "expired", retry: null };
    case "ROLEPLAY_GAME_NOT_FOUND":
      return { code: "notFound", retry: null };
    case "ROLEPLAY_GAME_CLOSED":
      return { code: "closed", retry: null };
    case "ROLEPLAY_NOT_PLAYABLE":
      return { code: "notPlayable", retry: null };
    case "ROLEPLAY_GAME_SUSPENDED":
      // A new start of a game genesis's catalogue pauses (75, 76): temporarily, never as if it could not be played.
      return { code: "suspended", retry: null };
    case "ROLEPLAY_UNSUPPORTED_CONTRACT":
    case "ROLEPLAY_UPDATE_NEEDED":
      return { code: "updateNeeded", retry: null };
    case "ROLEPLAY_NOT_SUPPORTED":
      return { code: "notSupported", retry: null };
    case "ROLEPLAY_UNAVAILABLE":
    case "ROLEPLAY_GENESIS_UNAVAILABLE":
      return { code: "unavailable", retry };
    case "ROLEPLAY_TEXT_REQUIRED":
      // A turn without words (96): the BFF or agent-service refused it before anything ran. spark sends none, but if one
      // gets through anyway the field gets the words back and says what a move needs.
      return { code: "textRequired", retry: null };
    default:
      return { code: "generic", retry };
  }
}

// A read after a stall or a refusal that did not settle: what the read said, or "check again".
function checkNotice(code: RoleplayErrorCode): Notice {
  const notice = noticeFor(code, "check");
  return notice.code === "generic" || notice.code === "unavailable" ? { code: "checkFailed", retry: "check" } : notice;
}

export function createRoleplaySession(gameId: string): RoleplaySession {
  const store = createRoleplayGameStore(gameId);
  const s = () => store.getState();
  // Every flow carries the epoch it started in; start and stop move it on, so a late answer finds itself stale.
  let epoch = 0;
  let active = false;
  const live = (at: number) => active && at === epoch;
  const guard = (at: number) => {
    if (!live(at)) throw new Stale();
  };
  const step = async <T>(at: number, work: Promise<T>): Promise<T> => {
    try {
      return await work;
    } finally {
      guard(at);
    }
  };
  const codeOf = (error: unknown): RoleplayErrorCode => {
    rethrowStale(error);
    return roleplayErrorCode(error);
  };
  const controllers = new Set<AbortController>();
  // One automatic attempt per owed narration (F6): after that only the player's retry tells it.
  const attempted = new Set<string>();
  // The epoch whose wait for the evaluation runs: every path that may end the story waits, and a second wait in the
  // same epoch would poll B4 alongside the first.
  let finishing: number | null = null;
  let turnAgent: HttpAgent | null = null;
  let narrationAgent: HttpAgent | null = null;

  const handlers = (at: number, report: (outcome: Outcome) => void): RoleplayRunHandlers => ({
    narration: (part, delta) => {
      if (live(at)) s().narrate(part, delta);
    },
    snapshot: (snapshot) => {
      if (live(at)) s().applySnapshot(snapshot);
    },
    progress: (phase) => {
      if (live(at)) s().setProgress(phase);
    },
    finished: (result) => report({ kind: "finished", result }),
    failed: (code, reason) => report({ kind: "failed", code, reason }),
    unknown: () => report({ kind: "unknown" }),
  });

  // One B5 or B6 run, aborted when the page leaves. A runner that throws stored nothing.
  async function stream(at: number, run: (abortController: AbortController, on: RoleplayRunHandlers) => Promise<void>): Promise<Outcome> {
    const controller = new AbortController();
    controllers.add(controller);
    let outcome = { kind: "unknown" } as Outcome;
    try {
      await step(at, run(controller, handlers(at, (reported) => (outcome = reported))));
    } catch (error) {
      rethrowStale(error);
      outcome = { kind: "failed", code: "UNKNOWN" };
    } finally {
      controllers.delete(controller);
    }
    return outcome;
  }

  // B4 until no turn or narration holds the game, for as long as the run that holds it is alive (§4.5, step 2).
  async function readUntilQuiet(at: number): Promise<Read> {
    let failingSince: number | null = null;
    for (;;) {
      const controller = new AbortController();
      controllers.add(controller);
      let response: GameResponse | null = null;
      try {
        response = await step(at, readGame(gameId, controller.signal));
      } catch (error) {
        const code = codeOf(error);
        if (!UNREACHABLE.has(code)) return { notice: checkNotice(code) };
        failingSince ??= Date.now();
        if (Date.now() - failingSince >= POLL_UNREACHABLE_MS) return { notice: NOT_RESPONDING };
      } finally {
        controllers.delete(controller);
      }
      if (response) {
        failingSince = null;
        if (!response.game.turnInProgress) return { response, runDied: false };
        if (!response.game.runAlive) return { response, runDied: true };
      }
      await step(at, sleep(POLL_INTERVAL_MS));
    }
  }

  // F6: whenever the page holds a game that owes a narration and nothing runs, tell it, with no button.
  async function tellOwed(at: number, game: GameSummary, retried = false): Promise<void> {
    const owed = game.narrationOwed;
    if (owed === null || s().notice?.code === "updateNeeded") return;
    if (game.status !== "open") return;
    const key = `${game.turn}:${game.scene}:${owed}`;
    if (attempted.has(key) && !retried) {
      s().setNotice({ code: "narrationFailed", retry: "narration" });
      return;
    }
    attempted.add(key);
    const runId = crypto.randomUUID();
    s().startNarration(runId);
    const outcome = await stream(at, (abortController, on) =>
      runRoleplayNarration((narrationAgent ??= createNarrationAgent(gameId)), { gameId, runId, expectedTurn: game.turn, abortController }, on),
    );
    if (outcome.kind === "finished") {
      s().finishRun(outcome.result);
      await tellOwed(at, s().game!);
      await waitForFinish(at);
    } else if (outcome.kind === "unknown") {
      await reconcile(at, "narration", runId);
    } else if (["ROLEPLAY_STALE_TURN", "ROLEPLAY_TURN_IN_PROGRESS", "ROLEPLAY_NO_NARRATION_OWED", "ROLEPLAY_GAME_CLOSED"].includes(outcome.code)) {
      // Someone else moved first (another tab), or it was told already: B4, then look again (§4.6).
      s().failNarration(null);
      await recheck(at);
    } else {
      // spark#1075, Entscheidung 125: an owed narration has no words of the player's to put differently, so whatever
      // reason the narrator gave, the retry tells it again.
      const notice = noticeFor(outcome.code, "narration");
      s().failNarration(notice.code === "generic" ? { code: "narrationFailed", retry: "narration" } : notice);
    }
  }

  /**
   * Follow the ordered outbox after the closing until Genesis accepts completion. Runs once per epoch: a call while
   * the epoch's wait runs leaves it to that one.
   */
  async function waitForFinish(at: number): Promise<void> {
    if (finishing === at) return;
    finishing = at;
    try {
      let failingSince: number | null = null;
      while (s().game?.storyEnded && s().game?.status === "open" && s().game?.narrationOwed === null) {
        await step(at, sleep(POLL_INTERVAL_MS));
        try {
          const response = await step(at, readGame(gameId));
          failingSince = null;
          s().apply(response);
        } catch (error) {
          rethrowStale(error);
          failingSince ??= Date.now();
          if (Date.now() - failingSince >= POLL_UNREACHABLE_MS) {
            s().setNotice(NOT_RESPONDING);
            return;
          }
        }
      }
    } finally {
      if (finishing === at) finishing = null;
    }
  }

  // The player's words, and only those: the scene's suggestions are orientation, never sent.
  async function send(at: number, client: ApolloClient, onLocked?: () => void, retryLock = true): Promise<void> {
    guard(at);
    const state = s();
    if (!canSend(state)) return;
    const game = state.game!;
    const text = turnText(state.draft.text);
    if (game.turn === 0) {
      state.setBusy();
      try {
        await step(at, lockRoleplaySessionPowers(client, gameId));
        onLocked?.();
      } catch (error) {
        rethrowStale(error);
        state.setIdle();
        state.setNotice({ code: "generic", retry: null });
        return;
      }
      state.setIdle();
    }
    const runId = crypto.randomUUID();
    state.startTurn(runId, { text });
    const outcome = await stream(at, (abortController, on) =>
      runRoleplayTurn((turnAgent ??= createTurnAgent(gameId)), { gameId, runId, text, expectedTurn: game.turn, abortController }, on),
    );
    if (outcome.kind === "finished") {
      s().finishRun(outcome.result);
      await tellOwed(at, s().game!);
      // The last turn may tell the ending in the same run: then nothing is owed, and only the evaluation is left.
      await waitForFinish(at);
      return;
    }
    if (outcome.kind === "unknown") {
      await reconcile(at, "turn", runId);
      return;
    }
    // Not stored: the input goes back, then each code as §5.1 says.
    switch (outcome.code) {
      case "ROLEPLAY_TURN_IN_PROGRESS":
      case "ROLEPLAY_NARRATION_OWED":
      case "ROLEPLAY_SCENE_ENDED":
      case "ROLEPLAY_GAME_CLOSED":
        s().failTurn(null);
        await recheck(at);
        return;
      case "ROLEPLAY_STALE_TURN":
        s().failTurn(null);
        await recheck(at);
        if (s().notice === null) s().setNotice({ code: "movedOn", retry: null });
        return;
      case "ROLEPLAY_POWERS_NOT_LOCKED":
        s().failTurn(null);
        if (retryLock) await send(at, client, onLocked, false);
        else s().setNotice({ code: "generic", retry: null });
        return;
      case "ROLEPLAY_NARRATOR_FAILED":
        // spark#1075, Entscheidung 125: the narrator gave up on these words after every rewrite (81). If its last
        // attempt was still refused or still leaked, the same words would most likely fail again: the notice asks the
        // player to describe the move differently. A model error may pass, so it asks to try again, as no reason at all
        // does.
        s().failTurn({ code: outcome.reason === "refused" || outcome.reason === "leak" ? "narratorRefused" : "narratorFailed", retry: null });
        return;
      case "ROLEPLAY_INTERNAL":
        s().failTurn({ code: "narratorFailed", retry: null });
        return;
      default:
        s().failTurn(noticeFor(outcome.code, null));
    }
  }

  // §4.5: a stall, a dropped connection, a result this build could not read, or a run that finished without its
  // snapshot. B4 says whether the run was stored.
  async function reconcile(at: number, kind: "turn" | "narration", runId: string): Promise<void> {
    s().startChecking();
    const read = await readUntilQuiet(at);
    if ("notice" in read) {
      s().checkFailed(read.notice);
      return;
    }
    // The run died holding the game: what it stored is stored; anything else it may never store, but the game stays
    // locked until its lease runs out, so the sent turn stays aside for the next check.
    if (read.runDied && !hasRun(read.response.log.entries, runId, kind === "turn" ? ["turn"] : ["opening", "closing"])) {
      s().checkFailed(NOT_RESPONDING);
      return;
    }
    if (kind === "turn") s().settleTurn(read.response, runId);
    else s().settleNarration(read.response, runId);
    await tellOwed(at, read.response.game);
    await waitForFinish(at);
  }

  // B4 after a refusal or while another tab plays, then look again: the game may owe a narration now.
  async function recheck(at: number): Promise<void> {
    s().startChecking();
    const read = await readUntilQuiet(at);
    if ("notice" in read) {
      s().checkFailed(read.notice);
      return;
    }
    if (read.runDied) {
      s().checkFailed(NOT_RESPONDING);
      return;
    }
    s().apply(read.response);
    await tellOwed(at, read.response.game);
    await waitForFinish(at);
  }

  async function show(at: number, response: GameResponse): Promise<void> {
    s().apply(response);
    if (response.game.turnInProgress) await recheck(at);
    else await tellOwed(at, response.game);
    await waitForFinish(at);
  }

  async function load(at: number): Promise<void> {
    s().setLoading();
    let response: GameResponse;
    try {
      response = await step(at, openGame(gameId));
    } catch (error) {
      s().failLoad(noticeFor(codeOf(error), "load"));
      return;
    }
    await show(at, response);
  }

  // Another tab may have played meanwhile (§4.5): read the game again when the page is looked at again.
  async function refresh(at: number): Promise<void> {
    guard(at);
    if (!s().loaded || s().activity !== "idle" || s().pending) return;
    let response: GameResponse;
    try {
      response = await step(at, readGame(gameId));
    } catch (error) {
      const code = codeOf(error);
      if (code === "ROLEPLAY_GAME_EXPIRED" || code === "ROLEPLAY_GAME_NOT_FOUND") s().setNotice(noticeFor(code, null));
      return;
    }
    // A turn the player sent while the read was on its way answers for itself.
    if (s().activity !== "idle") return;
    await show(at, response);
  }

  async function retry(at: number): Promise<void> {
    guard(at);
    const state = s();
    const action = state.notice?.retry;
    if (!action) return;
    if (action === "load") await load(at);
    else if (action === "narration") {
      state.setNotice(null);
      await tellOwed(at, state.game!, true);
    } else if (state.runId) await reconcile(at, state.pending ? "turn" : "narration", state.runId);
    else await recheck(at);
  }

  // F8: G2 by the game's key, then the page opens the new game (whose load is B3). genesis abandons the open session
  // and deletes its readings and chosen Powers, agent-service its story (100); the new session draws five Powers anew,
  // not locked (spark#1075, Entscheidung 127.49). Refused while the game is paused (O16).
  async function restart(at: number, client: ApolloClient): Promise<string | null> {
    guard(at);
    const state = s();
    const game = state.game;
    if (state.activity !== "idle" || (game?.status !== "open" && game?.status !== "finished")) return null;
    state.setBusy();
    try {
      const next = await step(at, restartRoleplaySession(client));
      s().setIdle();
      return next.session.id;
    } catch (error) {
      rethrowStale(error);
      s().setIdle();
      s().setNotice({ code: genesisRoleplayRefusal(error) === "suspended" ? "suspended" : "generic", retry: null });
      return null;
    }
  }

  // A new game after an expired one (13b, F3): continue the game's story if one is open meanwhile (F3), start one
  // (F2) that is not the expired session genesis still holds open (startPlaneGame), or say neutrally why it cannot
  // start, not yet or for now (§2.4, 76). The page opens the id.
  async function continueTo(at: number, client: ApolloClient): Promise<string | null> {
    guard(at);
    if (s().activity !== "idle") return null;
    s().setBusy();
    try {
      const catalogueEntry = gameEntry(await step(at, fetchCatalogue()));
      const entry = catalogueEntry.kind === "start" ? await step(at, startPlaneGame(client)) : catalogueEntry;
      if (entry.kind === "unavailable") {
        s().setIdle();
        s().setNotice({ code: entry.reason === "suspended" ? "suspended" : "notPlayable", retry: null });
        return null;
      }
      s().setIdle();
      return entry.gameId;
    } catch (error) {
      const code = codeOf(error);
      s().setIdle();
      s().setNotice(genesisRoleplayRefusal(error) === "suspended" ? { code: "suspended", retry: null } : noticeFor(code, null));
      return null;
    }
  }

  return {
    store,
    start: () => {
      active = true;
      epoch += 1;
      void unlessStale(load(epoch), undefined);
    },
    stop: () => {
      active = false;
      epoch += 1;
      for (const controller of controllers) controller.abort();
      controllers.clear();
    },
    refresh: () => unlessStale(refresh(epoch), undefined),
    send: (client, onLocked) => unlessStale(send(epoch, client, onLocked), undefined),
    retry: () => unlessStale(retry(epoch), undefined),
    restart: (client) => unlessStale(restart(epoch, client), null),
    continueTo: (client) => unlessStale(continueTo(epoch, client), null),
  };
}
